import React, { useState, useEffect } from 'react';
import { Plus, Settings, Zap, X, Search, Eye } from 'lucide-react';
import { apiService } from '../services/apiService';
import { useLanguage } from '../contexts/LanguageContext';
import { useFontSize } from '../contexts/FontSizeContext';
import { useDisplay } from '../contexts/DisplayContext';
import ManageSuggestionsModal from './ManageSuggestionsModal';

const QuickAddSection = ({ listId, onAddItem, canWrite = true, itemsCount = 0 }) => {
  const [quickItems, setQuickItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addingItems, setAddingItems] = useState(new Set());
  const [showSettings, setShowSettings] = useState(false);
  const [newItemName, setNewItemName] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [categorySuggestions, setCategorySuggestions] = useState([]);
  const [isTypingCategory, setIsTypingCategory] = useState(false);
  const [selectedCategoryIndex, setSelectedCategoryIndex] = useState(-1);
  const [preferences, setPreferences] = useState({ maxItems: 6, autoGenerate: true });
  const [showDebug, setShowDebug] = useState(false);
  const [showManageSuggestions, setShowManageSuggestions] = useState(false);
  const { t, getAllCategories } = useLanguage();
  const { getFontSizes } = useFontSize();
  const { getListCategorySetting, getListCategoryOption, updateListCategorySetting, getListQuickAddSetting, getListQuickAddOption, updateListQuickAddSetting } = useDisplay();

  useEffect(() => {
    loadQuickItems();
    loadPreferences();
  }, [listId]);

  // Reload quick add items when the main list items change
  useEffect(() => {
    if (listId && itemsCount >= 0) {
      loadQuickItems();
    }
  }, [itemsCount, listId]);

  const loadQuickItems = async () => {
    if (!listId) return;
    
    try {
      setLoading(true);
      const items = await apiService.getQuickAddItems(listId);
      setQuickItems(items || []);
    } catch (err) {
      console.error('Error loading quick add items:', err);
      setQuickItems([]);
    } finally {
      setLoading(false);
    }
  };

  const loadPreferences = async () => {
    try {
      const prefs = await apiService.getQuickAddPreferences();
      setPreferences(prefs);
    } catch (err) {
      console.error('Error loading preferences:', err);
    }
  };

  // Helper function to parse input for item name and category
  const parseInput = (input) => {
    const commaIndex = input.indexOf(',');
    if (commaIndex === -1) {
      return { itemName: input.trim(), category: '', isTypingCategory: false };
    }
    
    const itemName = input.substring(0, commaIndex).trim();
    const categoryPart = input.substring(commaIndex + 1);
    
    return {
      itemName,
      category: categoryPart.trim(),
      isTypingCategory: true,
      categorySearch: categoryPart // Keep original with spaces for search
    };
  };

  // Get user categories for autocomplete
  const getUserCategories = async (searchTerm = '') => {
    try {
      const response = await apiService.getUserCategories();
      let categories = response.categories || [];
      
      // Filter categories based on search term
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        categories = categories.filter(cat => 
          cat.toLowerCase().includes(search) || 
          cat.toLowerCase().startsWith(search)
        );
      }
      
      // Sort by relevance (starts with > contains > alphabetical)
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        categories.sort((a, b) => {
          const aLower = a.toLowerCase();
          const bLower = b.toLowerCase();
          
          // Exact match first
          if (aLower === search) return -1;
          if (bLower === search) return 1;
          
          // Starts with second
          const aStarts = aLower.startsWith(search);
          const bStarts = bLower.startsWith(search);
          if (aStarts && !bStarts) return -1;
          if (bStarts && !aStarts) return 1;
          
          // Alphabetical for same priority
          return a.localeCompare(b);
        });
      }
      
      return categories.slice(0, 2); // Limit to 2 suggestions for better mobile UX
    } catch (err) {
      console.error('Error getting user categories:', err);
      return [];
    }
  };

  const updatePreferences = async (newPrefs) => {
    try {
      const updated = await apiService.updateQuickAddPreferences(newPrefs);
      setPreferences(updated);
      // Reload quick items with new preferences
      loadQuickItems();
    } catch (err) {
      console.error('Error updating preferences:', err);
    }
  };

  const handleAddNewItem = async () => {
    if (!newItemName.trim()) return;
    
    // Parse the input to extract item name and category
    const parsed = parseInput(newItemName);
    const itemName = parsed.itemName;
    
    if (!itemName) return; // Need at least an item name
    
    try {
      // Determine category to use
      let category = 'General';
      let wasLearned = false;
      
      // If user specified a category after comma, use it (if valid)
      if (parsed.category) {
        console.log('🔍 User specified category:', parsed.category);
        const userCategories = await getUserCategories();
        console.log('🔍 Available user categories:', userCategories);
        const validCategory = userCategories.find(cat => 
          cat.toLowerCase() === parsed.category.toLowerCase()
        );
        console.log('🔍 Found valid category:', validCategory);
        if (validCategory) {
          category = validCategory;
          console.log('✅ Using specified category:', category);
        } else {
          console.log('❌ Category not found in user categories, trying learned category');
          // Category not found, try learned category
          try {
            const learnedData = await apiService.getLearnedCategory(itemName);
            if (learnedData.category) {
              category = learnedData.category;
              wasLearned = true;
              console.log('✅ Using learned category:', category);
            }
          } catch (err) {
            console.log('❌ Could not get learned category:', err);
          }
        }
      } else {
        // No category specified, try to learn from history
        try {
          const learnedData = await apiService.getLearnedCategory(itemName);
          if (learnedData.category) {
            category = learnedData.category;
            wasLearned = true;
          }
        } catch (err) {
          console.log('Could not get learned category');
        }
      }
      
      const result = await onAddItem({
        name: itemName,
        category: category,
        quantity: 1,
        notes: ''
      });
      
      // Log learning feedback for debugging
      if (wasLearned) {
        console.log(`🧠 Applied learned category: "${itemName}" → "${category}"`);
      } else if (parsed.category) {
        console.log(`👤 Used specified category: "${itemName}" → "${category}"`);
      }
      
      // Add to quick items for this list
      const newQuickItem = {
        name: itemName,
        category: category,
        quantity: 1,
        frequency: 1
      };
      
      setQuickItems(prev => {
        const existing = prev.find(item => item.name.toLowerCase() === itemName.toLowerCase());
        if (existing) {
          return prev.map(item => 
            item.name.toLowerCase() === itemName.toLowerCase() 
              ? { ...item, frequency: item.frequency + 1 }
              : item
          );
        } else {
          return [...prev, newQuickItem].slice(0, preferences.maxItems);
        }
      });
      
      // Save updated quick items
      try {
        await apiService.updateQuickAddItems(listId, quickItems);
      } catch (err) {
        console.log('Could not save quick items');
      }
      
      setNewItemName('');
      setSuggestions([]);
      setCategorySuggestions([]);
      setIsTypingCategory(false);
      setSelectedCategoryIndex(-1);
      
    } catch (err) {
      console.error('Error adding item:', err);
    }
  };

  const handleSearchChange = async (value) => {
    setNewItemName(value);
    setSelectedCategoryIndex(-1); // Reset selection when input changes
    
    // Parse input to check if user is typing category
    const parsed = parseInput(value);
    setIsTypingCategory(parsed.isTypingCategory);
    
    if (parsed.isTypingCategory) {
      // User is typing category after comma
      if (parsed.categorySearch.length >= 0) { // Show suggestions immediately when comma is typed
        try {
          const categories = await getUserCategories(parsed.categorySearch.trim());
          setCategorySuggestions(categories);
          setSuggestions([]); // Clear item suggestions
        } catch (err) {
          console.log('Could not get category suggestions');
          setCategorySuggestions([]);
        }
      } else {
        setCategorySuggestions([]);
      }
    } else {
      // User is typing item name
      setCategorySuggestions([]);
      
      if (parsed.itemName.length > 1) {
        try {
          // Get smart suggestions with improved scoring
          const smartSuggestions = await apiService.getSmartSuggestions(parsed.itemName, 5);
          
          // Enhance suggestions with learned categories and confidence scores
          const enhancedSuggestions = await Promise.all(
            smartSuggestions.map(async (suggestion) => {
              try {
                // Get the most up-to-date learned category
                const learnedData = await apiService.getLearnedCategory(suggestion.name);
                const enhanced = {
                  ...suggestion,
                  category: learnedData.category || suggestion.category,
                  confidence: suggestion.score || 0.5 // Use score from backend
                };
                
                // Debug logging for confidence scores
                console.log(`Suggestion: "${suggestion.name}" - Score: ${suggestion.score}, Confidence: ${enhanced.confidence}`);
                
                return enhanced;
              } catch (err) {
                return suggestion;
              }
            })
          );
          
          setSuggestions(enhancedSuggestions);
        } catch (err) {
          console.log('Could not get suggestions');
          setSuggestions([]);
        }
      } else {
        setSuggestions([]);
      }
    }
  };

  const handleSuggestionClick = async (suggestion) => {
    // Always add the suggested item directly when clicked
    try {
      const result = await onAddItem({
        name: suggestion.name,
        category: suggestion.category,
        quantity: suggestion.defaultQuantity || 1,
        notes: ''
      });
      
      // Log learning feedback
      const confidenceLevel = suggestion.confidence > 0.8 ? 'high' : 'normal';
      console.log(`🧠 Added suggested item (${confidenceLevel} confidence): "${suggestion.name}" → "${suggestion.category}"`);
      
      // Add to quick items for this list
      const newQuickItem = {
        name: suggestion.name,
        category: suggestion.category,
        quantity: suggestion.defaultQuantity || 1,
        frequency: 1
      };
      
      setQuickItems(prev => {
        const existing = prev.find(item => item.name.toLowerCase() === suggestion.name.toLowerCase());
        if (existing) {
          return prev.map(item => 
            item.name.toLowerCase() === suggestion.name.toLowerCase()
              ? { ...item, frequency: item.frequency + 1 }
              : item
          );
        } else {
          return [...prev, newQuickItem].slice(0, preferences.maxItems);
        }
      });
      
      // Save updated quick items
      try {
        await apiService.updateQuickAddItems(listId, quickItems);
      } catch (err) {
        console.log('Could not save quick items');
      }
      
      setNewItemName('');
      setSuggestions([]);
      setCategorySuggestions([]);
      setIsTypingCategory(false);
      setSelectedCategoryIndex(-1);
      
    } catch (err) {
      console.error('Error adding suggested item:', err);
    }
  };

  const handleCategorySuggestionClick = (category) => {
    // Replace the category part after comma with selected category
    const parsed = parseInput(newItemName);
    const newValue = `${parsed.itemName}, ${category}`;
    setNewItemName(newValue);
    setCategorySuggestions([]);
    setIsTypingCategory(false);
    setSelectedCategoryIndex(-1);
    
    // Focus back on input and position cursor at end
    setTimeout(() => {
      const input = document.querySelector('.quick-add-input');
      if (input) {
        input.focus();
        input.setSelectionRange(newValue.length, newValue.length);
      }
    }, 0);
  };

  const handleKeyDown = (e) => {
    if (isTypingCategory && categorySuggestions.length > 0) {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedCategoryIndex(prev => 
            prev < categorySuggestions.length - 1 ? prev + 1 : 0
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedCategoryIndex(prev => 
            prev > 0 ? prev - 1 : categorySuggestions.length - 1
          );
          break;
        case 'Tab':
          e.preventDefault();
          if (selectedCategoryIndex >= 0) {
            handleCategorySuggestionClick(categorySuggestions[selectedCategoryIndex]);
          } else if (categorySuggestions.length > 0) {
            handleCategorySuggestionClick(categorySuggestions[0]);
          }
          break;
        case 'Enter':
          if (selectedCategoryIndex >= 0) {
            e.preventDefault();
            handleCategorySuggestionClick(categorySuggestions[selectedCategoryIndex]);
          }
          break;
        case 'Escape':
          setCategorySuggestions([]);
          setIsTypingCategory(false);
          setSelectedCategoryIndex(-1);
          break;
      }
    }
  };

  const handleSeedLearningData = async () => {
    try {
      console.log('Attempting to seed learning data...');
      console.log('Auth token:', localStorage.getItem('authToken') ? 'Present' : 'Missing');
      
      const result = await apiService.seedLearningData();
      console.log('🌱 Learning data seeded:', result);
      alert(`Success! Seeded ${result.itemsLearned} items with ${result.totalLearningEvents} learning events!`);
    } catch (err) {
      console.error('Full error object:', err);
      console.error('Error stack:', err.stack);
      
      // More detailed error reporting
      let errorMessage = 'Error seeding learning data';
      if (err.message) {
        errorMessage += `\n\nDetails: ${err.message}`;
      }
      if (err.status) {
        errorMessage += `\n\nHTTP Status: ${err.status}`;
      }
      
      alert(errorMessage);
    }
  };

  const handleQuickAdd = async (item) => {
    console.log('🚀 QuickAdd: Attempting to add item:', item);
    console.log('🚀 QuickAdd: addingItems state:', addingItems);
    console.log('🚀 QuickAdd: canWrite prop:', canWrite);
    
    if (addingItems.has(item.name)) {
      console.log('🚀 QuickAdd: Item already being added, skipping');
      return;
    }
    
    setAddingItems(prev => new Set([...prev, item.name]));
    console.log('🚀 QuickAdd: Set item as adding:', item.name);
    
    try {
      console.log('🚀 QuickAdd: Calling onAddItem with:', {
        name: item.name,
        category: item.category,
        quantity: item.quantity || 1,
        notes: ''
      });
      
      const result = await onAddItem({
        name: item.name,
        category: item.category,
        quantity: item.quantity || 1,
        notes: ''
      });
      
      console.log('🚀 QuickAdd: onAddItem result:', result);
      console.log('🚀 QuickAdd: Item should now appear in list via real-time update');
      
      // Clear the input field and suggestions after successful add
      setNewItemName('');
      setSuggestions([]);
      setCategorySuggestions([]);
      setIsTypingCategory(false);
      setSelectedCategoryIndex(-1);
      
      // Brief success indication
      setTimeout(() => {
        console.log('🚀 QuickAdd: Removing item from adding state:', item.name);
        setAddingItems(prev => {
          const newSet = new Set(prev);
          newSet.delete(item.name);
          return newSet;
        });
      }, 1000);
      
    } catch (err) {
      console.error('🚀 QuickAdd: Error adding item:', err);
      setAddingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(item.name);
        return newSet;
      });
    }
  };

  // Group items by category
  const groupedItems = quickItems.reduce((groups, item) => {
    const category = item.category || 'General';
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(item);
    return groups;
  }, {});

  if (!canWrite) {
    console.log('🚀 QuickAdd: Not rendering - canWrite is false');
    return null; // Don't show quick add for read-only users
  }

  if (!listId) {
    console.log('🚀 QuickAdd: Not rendering - no listId provided');
    return null; // Need a list ID to work
  }

  if (preferences.maxItems === 0) {
    console.log('🚀 QuickAdd: Not rendering - maxItems is 0 (disabled)');
    return null; // User has disabled quick add
  }

  if (quickItems.length === 0 && !loading) {
    console.log('🚀 QuickAdd: Not rendering - no quick items and not loading');
    return null; // Don't show section if no quick items
  }

  console.log('🚀 QuickAdd: Rendering component with:', {
    canWrite,
    listId,
    preferences,
    quickItemsCount: quickItems.length,
    loading,
    quickAddSetting: getListQuickAddSetting(listId),
    quickItems: quickItems.map(item => ({ name: item.name, category: item.category }))
  });

  return (
    <div className="quick-add-section-mobile">
      <div className="quick-add-header">
        <div className="quick-add-header-left">
          <Zap size={16} />
          <span>{t('quickAdd', 'Quick Add')}</span>
        </div>
        <div className="quick-add-header-right">
          <span className="quick-add-count">({quickItems.length})</span>
          <button 
            className="quick-settings-btn"
            onClick={() => setShowSettings(!showSettings)}
            title={t('settings', 'Settings')}
          >
            <Settings size={14} />
          </button>
        </div>
      </div>
      
      {/* Quick Add Input */}
      <div className="quick-add-input-section">
        <div className="quick-add-input-container">
          <Search size={16} className="quick-add-search-icon" />
          <input
            type="text"
            placeholder={t('addQuickItem', 'Type item name, category...')}
            value={newItemName}
            onChange={(e) => handleSearchChange(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleAddNewItem()}
            onKeyDown={handleKeyDown}
            className="quick-add-input"
          />
          {newItemName && (
            <button 
              onClick={handleAddNewItem}
              className="quick-add-submit-btn"
            >
              <Plus size={16} />
            </button>
          )}
        </div>
        
        {/* Smart Suggestions */}
        {suggestions.length > 0 && (
          <div className="quick-suggestions">
            {suggestions.map((suggestion, index) => (
              <button
                key={index}
                onClick={() => handleSuggestionClick(suggestion)}
                className={`suggestion-item ${suggestion.confidence > 0.8 ? 'high-confidence' : ''}`}
                title={`${suggestion.name} - ${getAllCategories()[suggestion.category] || suggestion.category}${suggestion.frequency ? ` (used ${suggestion.frequency} times)` : ''}`}
              >
                <div className="suggestion-content">
                  <span className="suggestion-name">{suggestion.name}</span>
                  <span className="suggestion-category">{getAllCategories()[suggestion.category] || suggestion.category}</span>
                </div>
                {suggestion.confidence > 0.8 && (
                  <div className="suggestion-confidence">
                    <Zap size={12} />
                  </div>
                )}
                {suggestion.defaultQuantity > 1 && (
                  <span className="suggestion-quantity">×{suggestion.defaultQuantity}</span>
                )}
              </button>
            ))}
          </div>
        )}
        
        {/* Category Suggestions */}
        {categorySuggestions.length > 0 && isTypingCategory && (
          <div className="quick-category-suggestions">
            <div className="category-suggestions-header">
              <span>Categories:</span>
            </div>
            {categorySuggestions.map((category, index) => (
              <button
                key={index}
                onClick={() => handleCategorySuggestionClick(category)}
                className={`category-suggestion-item ${index === selectedCategoryIndex ? 'selected' : ''}`}
                title={`Select category: ${category}`}
              >
                <span className="category-suggestion-name">{category}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      
      {getListQuickAddSetting(listId) && (
        loading ? (
          <div className="quick-add-loading-mobile">
            <div className="loading-shimmer-mobile"></div>
          </div>
        ) : (
          <div className="quick-add-items-mobile">
            {quickItems.map((item, index) => {
              const isAdding = addingItems.has(item.name);
              
              console.log('🚀 QuickAdd: Rendering button for item:', item.name, 'category:', item.category, 'isAdding:', isAdding);
              
              return (
                <button
                  key={`${item.name}-${index}`}
                  onClick={(e) => {
                    console.log('🚀 QuickAdd: Button clicked for item:', item.name);
                    console.log('🚀 QuickAdd: Event:', e);
                    console.log('🚀 QuickAdd: Button element:', e.target);
                    e.preventDefault();
                    e.stopPropagation();
                    handleQuickAdd(item);
                  }}
                  disabled={isAdding}
                  className={`quick-item-mobile ${isAdding ? 'adding' : ''}`}
                  style={{ pointerEvents: 'auto', cursor: 'pointer' }}
                >
                  <span 
                    className="quick-item-name-mobile"
                    style={{ fontSize: getFontSizes().itemName, pointerEvents: 'none' }}
                  >
                    {item.name}
                  </span>
                  {isAdding && <div className="adding-spinner-mobile"></div>}
                </button>
              );
            })}
          </div>
        )
      )}
      
      {showSettings && (
        <div className="quick-add-settings">
          <div className="settings-header">
            <h4>{t('quickAddSettings', 'Quick Add Settings')}</h4>
            <button 
              onClick={() => setShowSettings(false)}
              className="settings-close"
            >
              <X size={16} />
            </button>
          </div>
          
          <div className="setting-item inline">
            <label htmlFor="maxItems">{t('maxItems', 'Number of items')}</label>
            <select 
              id="maxItems"
              value={preferences.maxItems}
              onChange={(e) => updatePreferences({ maxItems: parseInt(e.target.value) })}
            >
              <option value={0}>{t('disabled', 'Off')}</option>
              <option value={3}>3</option>
              <option value={6}>6</option>
              <option value={9}>9</option>
              <option value={12}>12</option>
              <option value={15}>15</option>
            </select>
          </div>
          
          <div className="setting-item checkbox">
            <label>
              <input 
                type="checkbox"
                checked={preferences.autoGenerate}
                onChange={(e) => updatePreferences({ autoGenerate: e.target.checked })}
              />
              {t('autoGenerate', 'Auto-generate from shopping history')}
            </label>
          </div>
          
          <div className="setting-item">
            <button 
              onClick={() => setShowManageSuggestions(true)}
              className="manage-suggestions-btn"
            >
              {t('manageSuggestions', 'Manage Suggestions')}
            </button>
            <small>{t('manageSuggestionsDesc', 'Review and delete unwanted suggestions from your shopping history')}</small>
          </div>
          
          <div className="setting-item">
            <label>{t('categoriesDisplay', 'Categories')}</label>
            <div className="category-display-options">
              <button 
                onClick={() => updateListCategorySetting(listId, 'global')}
                className={`category-option-btn ${getListCategoryOption(listId) === 'global' ? 'active' : ''}`}
              >
                {t('useGlobalSetting', 'Use Global Setting')}
              </button>
              <button 
                onClick={() => updateListCategorySetting(listId, 'show')}
                className={`category-option-btn ${getListCategoryOption(listId) === 'show' ? 'active' : ''}`}
              >
                <Eye size={14} />
                {t('alwaysShow', 'Always Show')}
              </button>
              <button 
                onClick={() => {
                  console.log('🖱️ Categories Hide button clicked for listId:', listId);
                  updateListCategorySetting(listId, 'hide');
                }}
                className={`category-option-btn ${getListCategoryOption(listId) === 'hide' ? 'active' : ''}`}
              >
                <Eye size={14} style={{ opacity: 0.5 }} />
                {t('alwaysHide', 'Always Hide')}
              </button>
            </div>
            <small>Current: {getListCategorySetting(listId) ? t('alwaysShow', 'Always Show') : t('alwaysHide', 'Always Hide')}</small>
          </div>

          <div className="setting-item">
            <label>{t('quickAddButtons', 'Quick Add Buttons')}</label>
            <div className="category-display-options">
              <button 
                onClick={() => updateListQuickAddSetting(listId, 'global')}
                className={`category-option-btn ${getListQuickAddOption(listId) === 'global' ? 'active' : ''}`}
              >
                {t('useGlobalSetting', 'Use Global Setting')}
              </button>
              <button 
                onClick={() => updateListQuickAddSetting(listId, 'show')}
                className={`category-option-btn ${getListQuickAddOption(listId) === 'show' ? 'active' : ''}`}
              >
                <Eye size={14} />
                {t('alwaysShow', 'Always Show')}
              </button>
              <button 
                onClick={() => {
                  console.log('🖱️ Quick Add Hide button clicked for listId:', listId);
                  updateListQuickAddSetting(listId, 'hide');
                }}
                className={`category-option-btn ${getListQuickAddOption(listId) === 'hide' ? 'active' : ''}`}
              >
                <Eye size={14} style={{ opacity: 0.5 }} />
                {t('alwaysHide', 'Always Hide')}
              </button>
            </div>
            <small>Current: {getListQuickAddSetting(listId) ? t('alwaysShow', 'Always Show') : t('alwaysHide', 'Always Hide')}</small>
          </div>
          
          <div className="settings-info">
            <p>{t('quickAddInfo', 'Items are automatically suggested based on your shopping patterns for this list.')}</p>
          </div>
          
          <div className="settings-debug">
            <button 
              onClick={() => setShowDebug(!showDebug)}
              className="debug-toggle"
            >
              🧪 Developer Tools {showDebug ? '▼' : '▶'}
            </button>
            
            {showDebug && (
              <div className="debug-content">
                <p>Test the smart learning system:</p>
                <button 
                  onClick={handleSeedLearningData}
                  className="debug-btn"
                >
                  🌱 Seed Learning Data
                </button>
                <small>Adds sample items with categories for testing suggestions</small>
              </div>
            )}
          </div>
        </div>
      )}
      
      {showManageSuggestions && (
        <ManageSuggestionsModal onClose={() => setShowManageSuggestions(false)} />
      )}
    </div>
  );
};

export default QuickAddSection;
